import React, { useState, useEffect, useCallback } from 'react';
import {
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar
} from '@mui/material';

import { apiService } from '../api/apiClient';
import { useTranslation } from 'react-i18next';

const ETLInterface = () => {
  const { t } = useTranslation();

  const [availableFiles, setAvailableFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeJobs, setActiveJobs] = useState([]);
  const [completedJobs, setCompletedJobs] = useState([]);
  const [showProcessDialog, setShowProcessDialog] = useState(false);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFileForUpload, setSelectedFileForUpload] = useState(null);
  const [processingOptions, setProcessingOptions] = useState({
    recursive: false,
    dataFormat: 'auto',
    batchSize: null
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showNotification = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  const loadJobStatus = useCallback(async () => {
    try {
      const statusResponse = await apiService.getStatus();
      setActiveJobs(statusResponse.active_jobs || []);
      setCompletedJobs(statusResponse.completed_jobs || []);
    } catch (err) {
      console.error('Error loading status:', err);
      const detail = err.response?.data?.detail;
      const line = t('content:etlErrorLoadStatus', {
        status: err.response?.status ?? '?',
        detail: detail || err.message,
      });
      setError((prev) => (prev ? `${prev} · ${line}` : line));
    }
  }, [t]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const filesResponse = await apiService.getAvailableFiles();
      setAvailableFiles(filesResponse || []);
    } catch (err) {
      console.error('Error loading files:', err);
      const detail = err.response?.data?.detail;
      setError(
        t('content:etlErrorListFiles', {
          status: err.response?.status ?? '?',
          detail: detail || err.message,
        })
      );
    }
    await loadJobStatus();
    setLoading(false);
  }, [t, loadJobStatus]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /** Poll job progress while ETL is running (backend updates progress between batches). */
  useEffect(() => {
    if (activeJobs.length === 0) return undefined;
    const id = setInterval(() => {
      loadJobStatus();
    }, 1500);
    return () => clearInterval(id);
  }, [activeJobs.length, loadJobStatus]);

  const startETLProcess = async () => {
    if (!selectedFile) {
      setError(t('content:etlSelectFilePrompt'));
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const request = {
        input_path: selectedFile,
        recursive: processingOptions.recursive,
        data_format: processingOptions.dataFormat,
        batch_size: processingOptions.batchSize
      };
      const response = await apiService.processETL(request);
      setShowProcessDialog(false);
      setSelectedFile('');
      await loadData();
      showNotification(t('content:etlProcessStarted', { jobId: response.job_id }), 'success');
    } catch (err) {
      console.error('Error starting ETL process:', err);
      setError(
        t('content:etlErrorStart', { detail: err.response?.data?.detail || err.message })
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) setSelectedFileForUpload(file);
  };

  const handleFileUpload = async () => {
    if (!selectedFileForUpload) {
      setError(t('content:etlSelectUploadPrompt'));
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const response = await apiService.uploadFile(selectedFileForUpload);
      setShowUploadDialog(false);
      setSelectedFileForUpload(null);
      await loadData();
      showNotification(t('content:etlUploadSuccess', { name: response.filename }), 'success');
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(t('content:etlErrorUpload', { detail: err.response?.data?.detail || err.message }));
    } finally {
      setUploading(false);
    }
  };

  const getJobStatusClass = (status) => {
    switch (status) {
      case 'completed': return 'text-signal-success border border-signal-success/30';
      case 'failed':    return 'text-signal-error border border-signal-error/30';
      case 'processing':
      case 'running':
      case 'pending':
        return 'text-accent-text border border-accent/30';
      default:          return 'text-ink-muted border border-border-strong';
    }
  };

  const getFileTypeBadgeClass = (type) => {
    switch (type) {
      case 'csv':  return 'text-accent-text border border-accent/30';
      case 'sql':  return 'text-ink-secondary border border-border-strong';
      case 'json': return 'text-ink-secondary border border-border-strong';
      default:     return 'text-ink-muted border border-border-strong';
    }
  };

  return (
    <div className="space-y-6">

      {/* Toolbar */}
      <div className="flex items-center gap-6">
        <button
          onClick={() => setShowUploadDialog(true)}
          disabled={loading}
          className="text-sm text-ink-secondary hover:text-ink-secondary disabled:opacity-40 transition-colors"
        >
          {t('content:etlUploadFile')}
        </button>
        <button
          onClick={() => setShowProcessDialog(true)}
          disabled={loading || availableFiles.length === 0}
          className="text-sm text-accent-text hover:text-accent-hover disabled:opacity-40 transition-colors font-medium text-white"
        >
          {t('content:etlNewProcess')}
        </button>
        <button
          onClick={loadData}
          disabled={loading}
          className="text-sm text-ink-muted hover:text-ink-secondary disabled:opacity-40 transition-colors"
        >
          {loading ? t('content:etlRefreshing') : t('content:etlRefresh')}
        </button>
      </div>

      {/* Error */}
      {error && (
        <p className="text-sm text-signal-error">{error}</p>
      )}

      {/* Content grid */}
      <div className="grid grid-cols-2 gap-8">

        {/* Archivos disponibles */}
        <div>
          <p className="text-label text-ink-muted uppercase tracking-widest mb-3">
            {t('content:etlAvailableFiles')}
          </p>
          <div className="divide-y divide-border-subtle">
            {availableFiles.length === 0 ? (
              <p className="text-sm text-ink-muted py-4">{t('content:etlNoFiles')}</p>
            ) : (
              availableFiles.map((file, index) => (
                <div key={index} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-ink-secondary truncate">{file.name}</p>
                    <p className="text-label text-ink-muted truncate">{file.path}</p>
                    <p className="text-label text-ink-muted">
                      {(file.size / 1024).toFixed(1)} KB · {new Date(file.modified * 1000).toLocaleDateString()}
                      {file.format &&
                        String(file.type).toLowerCase() !== String(file.format).toLowerCase() &&
                        ` · ${file.format}`}
                    </p>
                  </div>
                  <div className="flex gap-1.5 shrink-0">
                    <span className={`text-label px-2 py-0.5 rounded ${getFileTypeBadgeClass(file.type)}`}>
                      {file.type.toUpperCase()}
                    </span>
                    {file.format &&
                      String(file.type).toLowerCase() !== String(file.format).toLowerCase() && (
                      <span className="text-label text-ink-muted border border-border-strong px-2 py-0.5 rounded">
                        {file.format.toUpperCase()}
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Trabajos activos */}
        <div>
          <p className="text-label text-ink-muted uppercase tracking-widest mb-3">
            {t('content:etlActiveJobs', { count: activeJobs.length })}
          </p>
          <div className="divide-y divide-border-subtle">
            {activeJobs.length === 0 ? (
              <p className="text-sm text-ink-muted py-4">{t('content:etlNoActiveJobs')}</p>
            ) : (
              activeJobs.map((job, index) => {
                const pct = Math.min(100, Math.round(Number(job.progress ?? 0) * 100));
                return (
                <div key={job.job_id || index} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-ink-secondary">{t('content:etlJobLabel', { id: job.job_id })}</p>
                    <p className="text-label text-ink-muted truncate" title={job.input_path}>
                      {t('content:etlProgressLine', {
                        path: job.input_path || '—',
                        pct,
                      })}
                    </p>
                    <div className="mt-2 h-1.5 w-full max-w-md bg-surface-hover rounded overflow-hidden" aria-hidden>
                      <div
                        className="h-full bg-accent transition-[width] duration-500 ease-out"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    {(job.nodes_created > 0 || job.relationships_created > 0) && (
                      <p className="text-label text-ink-muted mt-1">
                        +{job.nodes_created ?? 0} nodes · +{job.relationships_created ?? 0} rels
                      </p>
                    )}
                  </div>
                  <span className={`text-label px-2 py-0.5 rounded shrink-0 ${getJobStatusClass(job.status)}`}>
                    {job.status}
                  </span>
                </div>
                );
              })
            )}
          </div>
        </div>

        {/* Trabajos completados — ancho completo */}
        <div className="col-span-2">
          <p className="text-label text-ink-muted uppercase tracking-widest mb-3">
            {t('content:etlCompletedJobs', { count: completedJobs.length })}
          </p>
          <div className="divide-y divide-border-subtle">
            {completedJobs.length === 0 ? (
              <p className="text-sm text-ink-muted py-4">{t('content:etlNoCompletedJobs')}</p>
            ) : (
              completedJobs.slice(0, 5).map((job, index) => (
                <div key={index} className="py-3 flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-ink-secondary">{t('content:etlJobLabel', { id: job.job_id })}</p>
                    <p className="text-label text-ink-muted truncate">
                      {job.status === 'completed'
                        ? t('content:etlCompletedLine', {
                            path: job.input_path,
                            duration: Math.round(job.duration || 0),
                            files: job.total_files ?? 0,
                            nodes: job.nodes_created ?? 0,
                            rels: job.relationships_created ?? 0,
                          })
                        : t('content:etlFailedLine', {
                            path: job.input_path,
                            error: job.error || t('content:etlErrorPipelineDefault'),
                          })}
                    </p>
                  </div>
                  <span className={`text-label px-2 py-0.5 rounded shrink-0 ${getJobStatusClass(job.status)}`}>
                    {job.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

      </div>

      {/* Dialog — Nuevo Proceso ETL */}
      <Dialog
        open={showProcessDialog}
        onClose={() => setShowProcessDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t('content:etlDialogNewTitle')}</DialogTitle>
        <DialogContent>
          <div className="pt-4 space-y-4">
            <FormControl fullWidth>
              <InputLabel>{t('content:etlDialogFileLabel')}</InputLabel>
              <Select
                value={selectedFile}
                onChange={(e) => setSelectedFile(e.target.value)}
                label={t('content:etlDialogFileLabel')}
              >
                {availableFiles.map((file, index) => (
                  <MenuItem key={index} value={file.path}>
                    {file.name} ({file.type.toUpperCase()})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <p className="text-label text-ink-muted uppercase tracking-widest pt-2">
              {t('content:etlProcessingOptions')}
            </p>

            <div className="grid grid-cols-2 gap-4">
              <FormControl fullWidth>
                <InputLabel>{t('content:etlFormatLabel')}</InputLabel>
                <Select
                  value={processingOptions.dataFormat}
                  onChange={(e) => setProcessingOptions(prev => ({ ...prev, dataFormat: e.target.value }))}
                  label={t('content:etlFormatLabel')}
                >
                  <MenuItem value="auto">{t('content:etlAutoDetect')}</MenuItem>
                  <MenuItem value="csv">CSV</MenuItem>
                  <MenuItem value="sql">SQL</MenuItem>
                  <MenuItem value="rdf">RDF</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>{t('content:etlBatchLabel')}</InputLabel>
                <Select
                  value={processingOptions.batchSize || ''}
                  onChange={(e) => setProcessingOptions(prev => ({
                    ...prev,
                    batchSize: e.target.value ? parseInt(e.target.value) : null
                  }))}
                  label={t('content:etlBatchLabel')}
                >
                  <MenuItem value="">{t('content:etlDefaultBatch')}</MenuItem>
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                  <MenuItem value={500}>500</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => setShowProcessDialog(false)}
            className="text-sm text-ink-muted hover:text-ink-secondary transition-colors px-4 py-2"
          >
            {t('content:etlCancel')}
          </button>
          <button
            onClick={startETLProcess}
            disabled={!selectedFile || loading}
            className="text-sm text-accent-text hover:text-accent-hover disabled:opacity-40 transition-colors font-medium px-4 py-2 flex items-center gap-2"
          >
            {loading && <CircularProgress size={12} />}
            {loading ? t('content:etlStarting') : t('content:etlStartProcess')}
          </button>
        </DialogActions>
      </Dialog>

      {/* Dialog — Subir archivo */}
      <Dialog
        open={showUploadDialog}
        onClose={() => setShowUploadDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('content:etlUploadDialogTitle')}</DialogTitle>
        <DialogContent>
          <div className="pt-4 space-y-4">
            <p className="text-sm text-ink-muted">
              {t('content:etlUploadFormatsHint')}
            </p>
            <label className="w-full border border-dashed border-border-strong hover:border-accent rounded p-6 text-sm text-ink-muted hover:text-ink-secondary transition-colors text-center cursor-pointer block">
              {selectedFileForUpload ? selectedFileForUpload.name : t('content:etlSelectFileLabel')}
              <input
                type="file"
                hidden
                accept=".sql,.csv,.json,.ttl,.rdf,.owl,.txt"
                onChange={handleFileSelect}
              />
            </label>
            {selectedFileForUpload && (
              <p className="text-label text-ink-muted">
                {(selectedFileForUpload.size / 1024).toFixed(1)} KB
              </p>
            )}
          </div>
        </DialogContent>
        <DialogActions>
          <button
            onClick={() => { setShowUploadDialog(false); setSelectedFileForUpload(null); }}
            className="text-sm text-ink-muted hover:text-ink-secondary transition-colors px-4 py-2"
          >
            {t('content:etlCancel')}
          </button>
          <button
            onClick={handleFileUpload}
            disabled={!selectedFileForUpload || uploading}
            className="text-sm text-accent-text hover:text-accent-hover disabled:opacity-40 transition-colors font-medium px-4 py-2 flex items-center gap-2"
          >
            {uploading && <CircularProgress size={12} />}
            {uploading ? t('content:etlUploading') : t('content:etlUploadButton')}
          </button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

    </div>
  );
};

export default ETLInterface;
